import { markFirstAdRequest } from '../../utils/consent/firstAdRequest'
import { render, h } from 'vue'
import { tryOnScopeDispose } from '@vueuse/core'
import { createRequestScopedComposable } from '../../utils/createRequestScopedComposable'
import Outbrain from '../../components/AdOps/Outbrain.vue'
import { getFallbackPlacementWidget } from '../../utils/ad-ops/adFunctions'
import loadScriptAsync from '../../utils/loadScriptAsync'
import type { FallbackTracking } from '../../utils/ad-ops/ayInstanceCodes'
import {
  consentSnapshot,
  subscribeToConsent,
  type ConsentDecision,
} from '../../utils/consent/consentGate'
import debug from '../../utils/console'
import type { NativeAdHandlers } from './useNativeAd'

const logger = debug.withTag('useOutbrainAds')

/**
 * Extract the brand/advertiser name from an Outbrain widget element.
 * Tries multiple selectors and strips "Sponsored:" prefix if present.
 */
export function getAdBrandFromElement(element: Element | null): string {
  if (!element) return ''

  const selectors = ['.ob-rec-source', '.ob-source', '.ob-author-name']
  const sponsoredPrefix = 'Sponsored:'

  for (const selector of selectors) {
    const brandEl = element.querySelector(selector)
    let brandText = brandEl?.textContent?.trim()

    if (brandText) {
      if (brandText.startsWith(sponsoredPrefix)) {
        brandText = brandText.substring(sponsoredPrefix.length).trim()
      }
      return brandText
    }
  }

  return ''
}

export interface OBExtern {
  extern: {
    researchWidget: () => unknown
  }
}

export interface OBREvent {
  name: 'rendered' | 'click'
  widgetId: string
  feedCards: number
  callback: (data: unknown) => void
}

export type OBR = OBExtern & Record<string, unknown>
declare global {
  interface Window {
    OBR: OBR | undefined
    OBREvents: OBREvent[] | undefined
  }
}

/**
 * Whether an event is this instance's. Its element is either our own
 * `.OUTBRAIN` div, which the SDK replaces, or a feed card in our wrapper.
 */
export type OutbrainOwns = (element: Element) => boolean

const TILE = '.ob-dynamic-rec-container, .ob-rec-box'

// Outbrain publishes no schema for the OBREvents payload, so every field is
// checked rather than assumed.
function readPayload(data: unknown) {
  const field = (key: string): unknown =>
    typeof data === 'object' && data !== null
      ? Reflect.get(data, key)
      : undefined
  const element = field('element')
  const cardIdx = field('cardIdx')
  const pointerEvent = field('pointerEvent')
  return {
    element: element instanceof Element ? element : null,
    cardIdx:
      typeof cardIdx === 'string' || typeof cardIdx === 'number'
        ? String(cardIdx)
        : null,
    pointerEvent: pointerEvent instanceof Event ? pointerEvent : null,
  }
}

// Only readable while the SDK is dispatching the click: the event's path is
// emptied once dispatch ends.
function clickedTile(event: Event | null) {
  const tile = event
    ?.composedPath()
    .find((node) => node instanceof Element && node.matches(TILE))
  if (!(tile instanceof Element)) return { tile: null, tileIdx: null }

  const siblings = tile.parentElement
    ? [...tile.parentElement.querySelectorAll(TILE)]
    : [tile]
  return { tile, tileIdx: siblings.indexOf(tile) }
}

/**
 * Registers one instance's `rendered` and `click` callbacks. Outbrain calls
 * every callback on a widget ID, and several instances can share one, so each
 * callback only passes on events `owns` says are this instance's.
 */
export function subscribeToOutbrain(
  widgetId: string,
  owns: OutbrainOwns,
  handlers: NativeAdHandlers
): void {
  window.OBREvents = window.OBREvents || []
  window.OBREvents.push({
    name: 'rendered',
    widgetId,
    feedCards: 1,
    callback: (data) => {
      const { element } = readPayload(data)
      if (element && owns(element)) handlers.onRender({ element })
    },
  })
  window.OBREvents.push({
    name: 'click',
    widgetId,
    feedCards: 1,
    callback: (data) => {
      const { element, cardIdx, pointerEvent } = readPayload(data)
      if (!element || !owns(element)) return
      // A click outside a recommendation, such as the "What is" footer
      // link, is not an ad click.
      const { tile, tileIdx } = clickedTile(pointerEvent)
      if (tile) handlers.onClick({ cardIdx, tileIdx, tile })
    },
  })
}

export function applyOutbrainConsent(
  root: ParentNode,
  decision: ConsentDecision
): boolean {
  const widgets = [
    ...(root instanceof Element && root.matches('.OUTBRAIN') ? [root] : []),
    ...root.querySelectorAll<HTMLElement>('.OUTBRAIN'),
  ]
  const ccpaString =
    decision.state !== 'allow' && decision.restrictions.nonPersonalizedAds
      ? '1YYN'
      : null
  let changed = false

  widgets.forEach((widget) => {
    if (widget.getAttribute('data-ccpa-string') === ccpaString) return

    changed = true
    if (ccpaString) {
      widget.setAttribute('data-ccpa-string', ccpaString)
    } else {
      widget.removeAttribute('data-ccpa-string')
    }
  })

  return changed
}

const useOutbrainAds = createRequestScopedComposable(() => {
  const isLoadedPromise = () =>
    loadScriptAsync(
      'head',
      {
        src: 'https://widgets.outbrain.com/outbrain.js',
        key: 'outbrain-homepage-header-script',
      },
      {
        id: 'outbrain-homepage-header-script',
        type: 'text/javascript',
        referrerpolicy: 'no-referrer-when-downgrade',
        async: true,
        'data-disableLocalStorageGdpr': 'true',
        onload: () => logger.debug('Outbrain script loaded.'),
        onerror: () => logger.error('Outbrain script failed to load.'),
      }
    )

  const callRenderResearchWidgets = async (): Promise<void> => {
    const consent = consentSnapshot()
    if (typeof document !== 'undefined' && consent) {
      applyOutbrainConsent(document, consent)
    }

    if (process.env.NODE_ENV !== 'test') {
      try {
        await isLoadedPromise()
      } catch (error) {
        logger.warn(
          'Failed to load Outbrain SDK, continuing without Outbrain ads',
          error
        )
        return
      }
    }

    if (
      !window.OBR ||
      !window.OBR.extern ||
      typeof window.OBR.extern.researchWidget !== 'function'
    ) {
      logger.warn(
        'Outbrain research widget function not available after script load'
      )
      return
    }
    // Render ad for empty widgets.
    markFirstAdRequest('outbrain')
    window.OBR.extern.researchWidget()
  }

  if (import.meta.client) {
    const unsubscribe = subscribeToConsent((decision) => {
      if (applyOutbrainConsent(document, decision)) {
        void callRenderResearchWidgets()
      }
    })
    const observer = new MutationObserver((mutations) => {
      const decision = consentSnapshot()
      if (!decision) return

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            applyOutbrainConsent(node, decision)
          }
        })
      })
    })
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    })
    tryOnScopeDispose(() => {
      unsubscribe()
      observer.disconnect()
    })
  }

  // Renders an Outbrain ad in a placement div. Used for programmatic ad fallback.
  const renderFallbackOutbrainAd = async (
    placement: string,
    element: Element | null,
    tracking?: FallbackTracking
  ): Promise<void> => {
    const widgetProps = getFallbackPlacementWidget(placement)

    if (!widgetProps.widgetId || !element) return

    // append fallback to the end of class to prog ads will no longer be mapped to the placement
    const newPlacementName = `${widgetProps.placement}-fallback`
    element.classList.remove(placement)
    element.classList.add(newPlacementName)

    // Erase the innerHTML of the placement div to clean scripts and other elements added by AY integration
    element.innerHTML = ''
    try {
      const { vueApp } = useNuxtApp()
      const outbrainNode = h(Outbrain, {
        ...widgetProps,
        placement: newPlacementName,
        ...tracking,
      })
      outbrainNode.appContext = vueApp._context
      // Insert the outbrain ad wrapper into the placement div
      render(outbrainNode, element)

      await callRenderResearchWidgets()
    } catch (err) {
      logger.error(
        `[Native-Fallback]: Error rendering fallback Outbrain ad component ${placement}`,
        err
      )
    }
  }

  // callRenderResearchWidgets only warns when outbrain.js failed to load;
  // waiting on the script turns that into a rejection the slot can report.
  const renderWidget = async (): Promise<void> => {
    await callRenderResearchWidgets()
    if (process.env.NODE_ENV !== 'test') {
      await isLoadedPromise()
    }
  }

  /**
   * Generate external secondary ID using Outbrain's recommended formula
   * This creates a unique value based on navigation timing and randomness
   * to ensure we stay within the 100k unique values/hour limit.
   *
   * Formula provides:
   * - Time-based component: navigationStart + now() for uniqueness across page loads
   * - Random component: Math.random() for uniqueness within same millisecond
   * - Scale factor: 1000000 to create sufficiently large numeric space
   *
   * @returns A numeric string representing a unique page view identifier
   */
  function generateExternalSecondaryId(): string {
    if (typeof window === 'undefined' || !window.performance?.timing) {
      // Fallback for SSR or environments without Performance API
      return Math.floor(Math.random() * 1000000000).toFixed()
    }
    return (
      (window.performance.timing.navigationStart + window.performance.now()) *
      Math.random() *
      1000000
    ).toFixed()
  }

  return {
    callRenderResearchWidgets,
    renderWidget,
    renderFallbackOutbrainAd,
    generateExternalSecondaryId,
  }
})

export default useOutbrainAds
